import { IonList, useIonAlert } from '@ionic/react';
import { Form, Formik } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Can, useHasPermission } from '../../api/authorization.provider';
import {
  ListTeamsDocument,
  ListTeamsQuery,
  Team,
  useCreateTeamMutation,
  useDisableTeamMutation,
  useEnableTeamMutation,
  useGetTeamLazyQuery,
  useListMachinesLazyQuery,
  useUpdateTeamMutation,
} from '../../api/graphql/generated';
import { trackCreatedTeam } from '../../config/analytics';
import { useRedirect } from '../../pages/redirect.hooks';
import { useBeaconRouter } from '../../route/route.hooks';
import { logger } from '../../util/logger';
import { RightAlignedButtons } from '../button/aligned-buttons';
import { BaseButton } from '../button/base-button';
import { DeleteButton } from '../button/delete-button';
import { FormSubmitButton } from '../form/form-submit.button';
import { IonFormField } from '../form/ion-form.field';
import { SaveStatusToast } from '../toast/toast.gql';

interface TeamSetupProps {
  id?: string;
}

const teamSchema = yup.object().shape({
  description: yup.string().nullable(),
  name: yup.string().required('*'),
});

const TeamSetup = (props: TeamSetupProps): ReactElement => {
  const { id } = props;
  const { i18n } = useTranslation();
  const [getTeam, { data }] = useGetTeamLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const team = useMemo(() => data?.getTeam, [data?.getTeam]);
  const [createTeamMutation, { loading, error }] = useCreateTeamMutation();
  const [updateTeamMutation, { loading: updateLoading, error: updateError }] =
    useUpdateTeamMutation();

  const [listMachines] = useListMachinesLazyQuery();
  const [disableTeam, { loading: disableLoading, error: disableError }] =
    useDisableTeamMutation();

  const [showAlert] = useIonAlert();

  const disable = useCallback(async () => {
    if (id === undefined) {
      return;
    }

    const activeMachinesList = await listMachines({
      variables: {
        input: {
          teamId: id,
          active: true,
        },
      },
    });

    const activeMachinesCount =
      activeMachinesList.data?.listMachines.items?.length ?? 0;

    if (activeMachinesCount > 0) {
      showAlert(i18n.t('common:team_deactivate_has_machines'));
      return;
    }

    disableTeam({
      variables: {
        input: {
          id,
        },
      },
      refetchQueries: [
        {
          query: ListTeamsDocument,
          variables: {
            input: {
              active: true,
            },
          },
        },
      ],
    });
  }, [disableTeam, i18n, id, listMachines, showAlert]);

  const [enableTeam, { loading: enableLoading, error: enableError }] =
    useEnableTeamMutation();
  const enable = useCallback(() => {
    if (id === undefined) {
      return;
    }

    enableTeam({
      variables: {
        input: {
          id,
        },
      },
      refetchQueries: [
        {
          query: ListTeamsDocument,
          variables: {
            input: {
              active: true,
            },
          },
        },
        {
          query: ListTeamsDocument,
          variables: {
            input: {
              active: false,
            },
          },
        },
      ],
    });
  }, [enableTeam, id]);

  const redirect = useRedirect();

  const { builder } = useBeaconRouter();

  const onSubmit = async (input: Partial<Team>) => {
    if (id) {
      const nextInput = {
        ...data?.getTeam,
        ...input,
      };
      /* eslint-disable @typescript-eslint/no-unused-vars */
      const { __typename, chatId, ...nextInputForUpdate } = nextInput;
      await updateTeamMutation({
        variables: {
          input: nextInputForUpdate,
        },
      });
    } else {
      try {
        const { chatId, ...remainingInputs } = input;
        const result = await createTeamMutation({
          variables: {
            input: remainingInputs,
          },
          update: (cache, { data: createTeamData }) => {
            const cacheData = cache.readQuery<ListTeamsQuery>({
              query: ListTeamsDocument,
              variables: { input: {} },
            });
            const items: Team[] = [...(cacheData?.listTeams.items ?? [])];
            if (createTeamData) {
              items.push(createTeamData?.createTeam);
            }
            cache.writeQuery({
              query: ListTeamsDocument,
              variables: { input: {} },
              data: {
                listTeams: {
                  items,
                  total: items.length,
                },
              },
            });
          },
          refetchQueries: [ListTeamsDocument],
        });

        trackCreatedTeam();

        const teamId = result.data?.createTeam.id;

        if (teamId) {
          redirect(builder.getTeamRoute(teamId));
        }

        logger.info('Successfully created team!');
      } catch (error) {
        logger.error('Error creating team', { error, input });
      }
    }
  };

  useEffect(() => {
    if (id) {
      getTeam({ variables: { input: { id } } });
    }
  }, [id, getTeam]);

  const canCreateTeam = useHasPermission('create', 'team', team);
  const canDeleteTeam = useHasPermission('delete', 'team', team);
  const showDisableButton = canDeleteTeam && team?.active;
  const showEnableButton = canCreateTeam && team?.active === false;

  return (
    <>
      <Formik
        validationSchema={teamSchema}
        onSubmit={onSubmit}
        initialValues={data?.getTeam ?? {}}
        enableReinitialize={true}
        validateOnMount={true}
      >
        {({ isValid, dirty }) => {
          return (
            <Form className="team-form">
              <IonList lines="none">
                <IonFormField
                  label={i18n.t('common:team_name')}
                  name="name"
                  inputProps={{
                    type: 'text',
                    autocorrect: 'on',
                    autocapitalize: 'on',
                  }}
                />
                <IonFormField
                  label={i18n.t('common:team_description')}
                  name="description"
                  inputProps={{
                    type: 'text',
                    autocorrect: 'on',
                    autocapitalize: 'on',
                  }}
                />
              </IonList>
              <br />
              <RightAlignedButtons>
                {showDisableButton && (
                  <DeleteButton
                    header={i18n.t('common:team_deactivate_confirm')}
                    onDelete={disable}
                    buttonProps={{
                      type: 'button',
                      disabled: false,
                    }}
                  >
                    {i18n.t('common:team_deactivate')}
                  </DeleteButton>
                )}
                {showEnableButton && (
                  <BaseButton
                    buttonProps={{
                      onClick: enable,
                    }}
                  >
                    {i18n.t('common:team_reactivate')}
                  </BaseButton>
                )}
                <Can I="update" a="team">
                  <FormSubmitButton
                    loading={loading}
                    disabled={!isValid || !dirty}
                    label={
                      id
                        ? i18n.t('common:team_update')
                        : i18n.t('common:team_create')
                    }
                    placement="none"
                  />
                </Can>
              </RightAlignedButtons>
            </Form>
          );
        }}
      </Formik>
      <SaveStatusToast
        loading={enableLoading}
        error={enableError}
        defaultMessageKey={'common:team_reactivate_success'}
        defaultErrorMessageKey={'common:team_reactivate_fail'}
      />
      <SaveStatusToast
        loading={disableLoading}
        error={disableError}
        defaultMessageKey={'common:team_deactivate_success'}
        defaultErrorMessageKey={'common:team_deactivate_fail'}
      />
      <SaveStatusToast loading={loading} error={error} />
      <SaveStatusToast loading={updateLoading} error={updateError} />
    </>
  );
};

export { TeamSetup };
